import { useEffect, useRef, useState } from "react";
import { ActionIcon, Box, Button, Group, Image, Stack, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { IconCamera, IconPhotoUp, IconX } from "@tabler/icons-react";
import { imageToDataUri } from "@passio/api-client";
import { sampleDataUri, type SamplePhoto } from "../lib/samples";

/** Drop, paste, pick or capture an image; returns a downscaled JPEG data URI. */
export function ImagePicker({
  value,
  onChange,
  label = "Drop a photo here, paste one, or choose a file",
  samples,
}: {
  value: string | null;
  onChange: (dataUri: string | null) => void;
  label?: string;
  /** Bundled photos offered as one-click examples. */
  samples?: SamplePhoto[];
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const camRef = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);

  const accept = async (file?: File | null) => {
    if (!file || !file.type.startsWith("image/")) return;
    setBusy(true);
    try {
      onChange(await imageToDataUri(file));
    } finally {
      setBusy(false);
    }
  };

  const pickSample = async (src: string) => {
    setBusy(true);
    try {
      onChange(await sampleDataUri(src));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const f = [...(e.clipboardData?.files ?? [])].find((x) => x.type.startsWith("image/"));
      if (f) void accept(f);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (value) {
    return (
      <Box pos="relative" w="fit-content">
        <Image src={value} alt="Selected" mah={320} radius="lg" fit="contain" />
        <ActionIcon pos="absolute" top={8} right={8} radius="xl" color="ink.8" onClick={() => onChange(null)} aria-label="Remove image">
          <IconX size={16} />
        </ActionIcon>
      </Box>
    );
  }
  return (
    <Box
      className="pd-dropzone"
      data-active={active}
      p="xl"
      onDragOver={(e) => {
        e.preventDefault();
        setActive(true);
      }}
      onDragLeave={() => setActive(false)}
      onDrop={(e) => {
        e.preventDefault();
        setActive(false);
        void accept(e.dataTransfer.files?.[0]);
      }}
    >
      <Stack align="center" gap="sm">
        <IconPhotoUp size={36} color="var(--pd-violet)" />
        <Text fw={600} ta="center">
          {label}
        </Text>
        <Text fz="xs" c="dimmed">
          Images are resized to 1280px JPEG in your browser before upload.
        </Text>
        <Group gap="xs">
          <Button variant="default" onClick={() => fileRef.current?.click()} loading={busy}>
            Choose file
          </Button>
          <Button variant="light" color="violet" leftSection={<IconCamera size={16} />} onClick={() => camRef.current?.click()}>
            Take photo
          </Button>
        </Group>
        {samples?.length ? (
          <Stack gap={6} align="center" mt={4}>
            <Text fz="xs" c="dimmed">
              No photo handy? Try a sample:
            </Text>
            <Group gap="xs" justify="center">
              {samples.map((s) => (
                <Tooltip key={s.id} label={s.note} multiline w={220} withArrow>
                  <UnstyledButton onClick={() => void pickSample(s.src)} disabled={busy} className="pd-tile" style={{ borderRadius: 10, padding: 4 }} aria-label={`Use the ${s.label} sample`}>
                    <Group gap={6} wrap="nowrap">
                      <Image src={s.src} alt="" w={40} h={40} radius="md" fit="cover" />
                      <Text fz="xs" fw={600} pr={6}>
                        {s.label}
                      </Text>
                    </Group>
                  </UnstyledButton>
                </Tooltip>
              ))}
            </Group>
          </Stack>
        ) : null}
      </Stack>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => void accept(e.currentTarget.files?.[0])} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => void accept(e.currentTarget.files?.[0])} />
    </Box>
  );
}
